import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setupApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import StatusBadge from "@/components/StatusBadge";
import { SETUP } from "@/constants/testIds";
import {
  Settings2, Building2, ShieldCheck, Landmark, Calendar, MessageSquare, Bot, Network,
  BadgeDollarSign, ChevronRight, AlertCircle, CheckCircle2, Clock, Zap, RefreshCw,
  Layers, LayoutDashboard,
} from "lucide-react";

const DOMAIN_ICONS = {
  "01-TENANT-PROFILE": Building2,
  "02-ORGANIZATION": Network,
  "03-IDENTITY-SECURITY": ShieldCheck,
  "04-BASE-FINANCE": Landmark,
  "05-EVENT-MODEL": Calendar,
  "06-COMMUNICATION": MessageSquare,
  "07-AGENT-GOVERNANCE": Bot,
  "08-PRICING-STUDIO": BadgeDollarSign,
};

const DOMAIN_CONFIG_PATHS = {
  "02-ORGANIZATION": "/organization",
  "03-IDENTITY-SECURITY": "/administration",
  "04-BASE-FINANCE": "/finance",
  "05-EVENT-MODEL": "/event-model",
  "06-COMMUNICATION": "/email-channel",
  "07-AGENT-GOVERNANCE": "/agent-governance",
  "08-PRICING-STUDIO": "/pricing-studio",
};

export default function SetupStudio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const brand = useBrand();
  const [domains, setDomains] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([setupApi.listDomains(), setupApi.getReadiness()])
      .then(([d, r]) => { setDomains(d.data); setReadiness(r.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <LoadingState />;

  const overall = readiness?.overall_percentage || 0;
  const activeDomains = readiness?.active_domains || 0;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid={SETUP.studioHome}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Settings2 className="w-5 h-5 text-[#0176D3]" />
            <h1 className="text-2xl font-bold text-[#181818] font-sans">Setup Studio</h1>
          </div>
          <p className="text-sm text-[#706E6B]">{brand.productName} — tenant configuration and activation control plane</p>
          <p className="text-xs font-mono text-[#706E6B] mt-1">{user?.tenant_name || user?.tenant_id}{user?.tenant_number ? ` · ${user.tenant_number}` : ""}</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg border border-[#E5E5E5] text-[#706E6B] hover:text-[#0176D3] hover:border-[#0176D3]/40 transition-all">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Overall Readiness Bar */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs font-mono text-[#706E6B] uppercase tracking-widest">Overall Platform Readiness</p>
            <p className="text-3xl font-bold text-[#181818] font-sans mt-1" data-testid={SETUP.overallReadiness}>{overall}%</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#706E6B]">Active Domains</p>
            <p className="text-2xl font-bold text-[#2E844A] font-sans" data-testid={SETUP.activeDomainCount}>{activeDomains}<span className="text-[#706E6B] text-lg">/{readiness?.total_domains || 8}</span></p>
          </div>
        </div>
        <div className="h-3 bg-[#F3F3F3] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#0176D3] to-[#014486] rounded-full transition-all duration-700"
            style={{ width: `${overall}%` }} />
        </div>
        <p className="text-xs text-[#706E6B] mt-2 font-mono">Formula: (task completion × lifecycle stage × dependency satisfaction) / 7 domains</p>
      </div>

      {/* Domain Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {domains.map(domain => {
          const Icon = DOMAIN_ICONS[domain.code] || Settings2;
          const configPath = DOMAIN_CONFIG_PATHS[domain.code];
          const isActive = domain.status === "ACTIVE";
          const blockerCount = domain.blocker_count || 0;
          const pct = domain.readiness?.percentage || 0;
          return (
            <div
              key={domain.code}
              data-testid={`${SETUP.domainCard}-${domain.code}`}
              className={`bg-[#F3F3F3] border rounded-xl p-5 card-hover cursor-pointer transition-all ${
                isActive ? "border-emerald-500/30" : blockerCount > 0 ? "border-rose-500/20" : "border-[#E5E5E5]"
              }`}
              onClick={() => navigate(`/setup-studio/${domain.code}`)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isActive ? "bg-emerald-500/10" : "bg-[#EEF4FF]"}`}>
                  <Icon className={`w-4 h-4 ${isActive ? "text-[#2E844A]" : "text-[#0176D3]"}`} />
                </div>
                <StatusBadge status={domain.status} data-testid={`${SETUP.domainStatus}-${domain.code}`} />
              </div>

              <h3 className="text-sm font-semibold text-[#181818] font-sans mb-1">{domain.name}</h3>
              <p className="text-xs text-[#706E6B] mb-4 line-clamp-2">{domain.description}</p>

              {/* Readiness */}
              <div className="mb-3">
                <div className="flex justify-between text-[10px] font-mono text-[#706E6B] mb-1">
                  <span>Readiness</span>
                  <span data-testid={`${SETUP.readinessPct}-${domain.code}`} className="text-[#444444]">{pct}%</span>
                </div>
                <div className="h-1.5 bg-[#F3F3F3] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-500 ${isActive ? "bg-emerald-400" : "bg-[#0176D3]"}`}
                    style={{ width: `${pct}%` }} />
                </div>
              </div>

              {/* Stats row */}
              <div className="flex items-center gap-3 text-[10px] font-mono">
                {blockerCount > 0 ? (
                  <span className="flex items-center gap-1 text-rose-400">
                    <AlertCircle className="w-3 h-3" /> {blockerCount} blockers
                  </span>
                ) : isActive ? (
                  <span className="flex items-center gap-1 text-[#2E844A]">
                    <CheckCircle2 className="w-3 h-3" /> Ready
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-[#706E6B]">
                    <Clock className="w-3 h-3" /> {domain.status}
                  </span>
                )}
                {domain.dependencies?.length > 0 && (
                  <span className="text-[#706E6B]">{domain.dependencies.filter(d => d.is_satisfied).length}/{domain.dependencies.length} deps</span>
                )}
                <span className="ml-auto text-[#0176D3] flex items-center gap-0.5">
                  Configure <ChevronRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Lifecycle Legend */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4">
        <p className="text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-3">Setup Lifecycle</p>
        <div className="flex flex-wrap gap-2">
          {["NOT_STARTED","IN_PROGRESS","CONFIGURED","VALIDATED","APPROVED","ACTIVE"].map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <StatusBadge status={s} />
              {i < 5 && <ChevronRight className="w-3 h-3 text-[#444444]" />}
            </div>
          ))}
        </div>
      </div>

      {/* Quick links — operational configuration */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4">
        <p className="text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-3">Operational Configuration</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          <div
            onClick={() => navigate("/event-ops-mdm")}
            data-testid="ops-mdm-setup-card"
            className="bg-white border border-[#E5E5E5] rounded-xl p-4 cursor-pointer hover:border-[#0176D3]/40 hover:shadow-sm transition-all"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#EEF4FF] flex items-center justify-center flex-shrink-0">
                <Layers className="w-4 h-4 text-[#0176D3]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#181818]">Event Operations MDM</p>
                <p className="text-xs text-[#706E6B] mt-0.5">Thread templates · Task templates · Exception categories · Escalation rules · Delivery criteria · Milestones</p>
              </div>
            </div>
            <div className="flex items-center justify-end mt-2">
              <span className="text-[10px] text-[#0176D3] flex items-center gap-0.5 font-mono">Configure <ChevronRight className="w-3 h-3" /></span>
            </div>
          </div>
          <div
            onClick={() => navigate("/homepage-admin")}
            data-testid="homepage-admin-setup-card"
            className="bg-white border border-[#E5E5E5] rounded-xl p-4 cursor-pointer hover:border-[#C9A15F]/40 hover:shadow-sm transition-all"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#FBF5EC] flex items-center justify-center flex-shrink-0">
                <LayoutDashboard className="w-4 h-4 text-[#C9A15F]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#181818]">Homepage Configuration</p>
                <p className="text-xs text-[#706E6B] mt-0.5">Category visibility · Public descriptions · Featured partners · Display caps · Display order</p>
              </div>
            </div>
            <div className="flex items-center justify-end mt-2">
              <span className="text-[10px] text-[#C9A15F] flex items-center gap-0.5 font-mono">Configure <ChevronRight className="w-3 h-3" /></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="p-6 space-y-6 animate-pulse">
      <div className="h-8 bg-[#F3F3F3] rounded-lg w-64" />
      <div className="h-24 bg-[#F3F3F3] rounded-xl" />
      <div className="grid grid-cols-3 gap-4">{[...Array(7)].map((_, i) => <div key={i} className="h-44 bg-[#F3F3F3] rounded-xl" />)}</div>
    </div>
  );
}
